import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import Fab from '@mui/material/Fab';
import { Add as AddIcon } from '@mui/icons-material';
import FormControl from '@mui/material/FormControl';
import {
    TextInput,
    DateInput,
    Button,
    Services,
    Text,
    Modal,
    BubbleSelectBlock,
    Theme,
    Bubble
} from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';

import * as notifier from '../../actions/notifier.js';
import PersonBlock from '../../components/PersonBlock.js';
import {
    addDependent,
    editDependent,
    removeDependent,
    getDependentTerminatedBenefits,
    terminateDependentBenefits,
    reinstateDependentBenefits,
    resetDependentPassword
} from '../../actions/users.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';

const insuranceOptions = [
    'Simply Benefits',
    'Desjardins',
    'Great-West Life',
    'Green Shield',
    'Manulife',
    'Pacific Blue Cross',
    'RBC Insurance',
    'RWAM',
    'SSQ',
    'Sun Life',
    'The Co-operators',
    'Other'
];

const benefits = {
    lifeInsurance: 'Life Insurance',
    deathDismemberment: 'Death Dismemberment',
    dependentLife: 'Dependent Life',
    ltd: 'LTD',
    std: 'STD',
    criticalIllness: 'Critical Illness',
    healthcare: 'Healthcare',
    outOfCountry: 'Out Of Country',
    efap: 'EFAP',
    virtualHealthcare: 'Virtual Healthcare',
    financialAwareness: 'Financial Awareness',
    dental: 'Dental'
};

const primaryBenefitsNames = [
    'Drugs',
    'Out of Country & Province',
    'Dental',
    'Major Medical',
    'Paramedical',
    'Vision'
];

class Dependents extends PureComponent {
    state = {
        firstname: '',
        lastname: '',
        gender: '',
        birthdate: '',
        cohabDate: '',
        dateOfMarriage: '',
        relationship: '',
        status: '',
        email: '',
        employer: null,
        insuranceCarrier: null,
        dependentCertNumber: null,
        spouseCoverage: false,
        hasProvincialHealthCoverage: false,
        isStudent: false,
        dependents: null,
        dialogOpen: false,
        isEdit: false,
        modalTitle: null,
        hasSpouse: false,
        options: Services.selectFieldOptions.dependentOptionsWithoutSpouse(),
        dependentID: null,
        dependentToRemoveID: false,
        showEffectiveDateDialog: false,
        showTerminationEffectiveDateDialog: false,
        effectiveDate: dates.getUTC(null, 'YYYY/MM/DD'),
        isReinstatement: false,
        benefitsToTerminate: [],
        benefitsToReinstate: [],
        terminatedBenefits: [],
        terminateBenefitsEffectiveDateDialogOpen: false,
        reinstateBenefitsEffectiveDateDialogOpen: false,
        isDisabled: false, // has a disability,
        primaryBenefits: []
    };

    async componentDidMount() {
        await this.props.dependents.some((dependent) => {
            if (dependent.relationship.toUpperCase() === 'SPOUSE') {
                this.setState({
                    hasSpouse: dependent.dependentID
                });

                return true;
            }

            return false;
        });
    }

    _close = () => {
        this.setState({
            firstnameError: null,
            lastnameError: null,
            genderError: null,
            relationshipError: null,
            emailError: null,
            isEdit: false,
            modalTitle: null,
            dialogOpen: false,
            dependentToRemoveID: false,
            showEffectiveDateDialog: false,
            showTerminationEffectiveDateDialog: false,
            effectiveDate: dates.getUTC(null, 'YYYY/MM/DD'),
            isReinstatement: false,
            manageBenefitsDialogOpen: false,
            benefitsToTerminate: [],
            benefitsToReinstate: [],
            terminateBenefitsEffectiveDateDialogOpen: false,
            reinstateBenefitsEffectiveDateDialogOpen: false
        });
    };

    _handleSelectChange = (type, event, id) => {
        if (event.target.value.toUpperCase() === 'SPOUSE') {
            this.setState({
                hasSpouse: id
            });
        }

        this._handleInputChange(type, event);
    };

    _onBlur = async (type) => {
        if (type === 'email' && this.state[type].length === 0) {
            return;
        }

        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };

    _getEligibilityDate = (cohabDate) => {
        if (cohabDate.length === 0) {
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            return dates.getStartOfDay(oneYearFromNow);
        }

        const oneYearFromCohabDate = new Date(cohabDate);
        oneYearFromCohabDate.setFullYear(oneYearFromCohabDate.getFullYear() + 1);
        return dates.getStartOfDay(oneYearFromCohabDate);
    };

    _isDependentEligible = (cohabDate, eligibilityDate) => {
        const now = dates.getUTC();
        return eligibilityDate < now;
    };

    _validateForm = async () => {
        const firstnameError = await Services.validateInput('firstname', this.state.firstname || '');
        const lastnameError = await Services.validateInput('lastname', this.state.lastname || '');
        const genderError = await Services.validateInput('gender', this.state.gender || '');
        const birthdateError = await Services.validateInput('birthdate', this.state.birthdate || '');
        let cohabDateError = this.state.relationship.toUpperCase() === 'COMMON LAW' ? await Services.validateInput('relationship', this.state.cohabDate) : false;
        let dateOfMarriageError = this.state.relationship.toUpperCase() === 'SPOUSE' ? await Services.validateInput('birthdate', this.state.dateOfMarriage) : false;
        const relationshipError = await Services.validateInput('relationship', this.state.relationship || '');
        const emailError = (!this.state.email || this.state.email === '') ? false : await Services.validateInput('email', this.state.email || '');

        let employerError = false;
        let insuranceCarrierError = false;
        let dependentCertNumberError = false;

        if (this.state.relationship.toUpperCase() !== 'CHILD' && this.state.spouseCoverage === true) {
            employerError = await Services.validateInput('checkEmpty', this.state.employer || '');
            insuranceCarrierError = await Services.validateInput('checkEmpty', this.state.insuranceCarrier || '');
            dependentCertNumberError = await Services.validateInput('checkEmpty', this.state.dependentCertNumber || '');
        }

        if (this.state.relationship.toUpperCase() === 'COMMON LAW') {
            const eligibilityDate = this._getEligibilityDate(this.state.cohabDate);
            const isEligible = this._isDependentEligible(this.state.cohabDate, eligibilityDate);

            if (!isEligible) {
                cohabDateError = `This dependent is not eligible, they must be cohabiting for at least 12 months. The dependent will be eligible on: ${ dates.getStartOfDay(eligibilityDate, 'll') }`;
            }
        }

        this.setState({
            firstnameError,
            lastnameError,
            genderError,
            birthdateError,
            cohabDateError,
            dateOfMarriageError,
            relationshipError,
            emailError,
            employerError,
            insuranceCarrierError,
            dependentCertNumberError
        });

        if (
            firstnameError ||
            lastnameError ||
            genderError ||
            birthdateError ||
            cohabDateError ||
            dateOfMarriageError ||
            relationshipError ||
            emailError ||
            employerError ||
            insuranceCarrierError ||
            dependentCertNumberError
        ) {
            return false;
        }

        const isValid = await this._ageValidation();

        if (isValid) {
            return true;
        }

        return false;
    };

    _handleBubbleListSelector = async (option) => {
        const selection = [ ...this.state.primaryBenefits ];
        const index = selection.findIndex((e) => e === option);

        if (index === -1) {
            selection.push(option);
        } else {
            selection.splice(index, 1);
        }

        await this._handleInputChange('primaryBenefits', selection, true);
    };

    _openEffectiveDateDialog = async () => {
        const isFormValid = await this._validateForm();

        if (!isFormValid) {
            return null;
        }

        // if the account is not yet setup then we don't need to ask for an effective date
        if (this.props.meta.accountEffDate === null) {
            return this._save();
        }

        this.setState({
            dialogOpen: false,
            showEffectiveDateDialog: true
        });

        return null;
    };

    _closeEffectiveDateDialog = () => {
        this.setState({
            showEffectiveDateDialog: false
        });
    };

    _add = () => {
        let options = Services.selectFieldOptions.dependentOptionsWithoutSpouse();

        if (!this.state.hasSpouse) {
            options = Services.selectFieldOptions.dependentOptionsWithSpouse();
        }

        this.setState({
            firstname: '',
            lastname: '',
            gender: '',
            birthdate: '',
            dateOfMarriage: '',
            cohabDate: '',
            relationship: '',
            email: '',
            employee: '',
            insuranceCarrier: '',
            dependentCertNumber: '',
            spouseCoverage: false,
            isStudent: false,
            isEdit: false,
            modalTitle: "Add Dependent",
            firstnameError: false,
            lastnameError: false,
            birthdateError: false,
            cohabDateError: false,
            hasProvincialHealthCoverage: false,
            dateOfMarriageError: false,
            relationshipError: false,
            emailError: false,
            dialogOpen: true,
            dependentToRemoveID: false,
            showEffectiveDateDialog: false,
            showTerminationEffectiveDateDialog: false,
            effectiveDate: dates.getUTC(null, 'YYYY/MM/DD'),
            options,
            isDisabled: false,
            primaryBenefits: []
        });
    };

    _edit = async (dependent, reinstatement = false) => {
        let options = Services.selectFieldOptions.dependentOptionsWithoutSpouse();

        if (!this.state.hasSpouse || this.state.hasSpouse === dependent.dependentID) {
            options = Services.selectFieldOptions.dependentOptionsWithSpouse();
        }

        await this.setState({
            dependentID: dependent.dependentID,
            firstname: dependent.firstname,
            lastname: dependent.lastname,
            gender: dependent.gender,
            birthdate: Services.dates.formatIncomingDate(dependent.birthdate),
            cohabDate: dependent.cohabDate === '' ? null : Services.dates.formatIncomingDate(dependent.cohabDate),
            relationship: dependent.relationship,
            email: dependent.email || '',
            employer: dependent.employer || '',
            insuranceCarrier: dependent.insuranceCarrier || '',
            dependentCertNumber: dependent.cert || '',
            status: dependent.status,
            isStudent: dependent.isStudent,
            spouseCoverage: dependent.relationship.toUpperCase() === 'SPOUSE' || dependent.relationship.toUpperCase() === 'COMMON LAW' ? Boolean(this.props.user.coverageThroughSpouse) : false, // only use the coverageThroughSpouse flag if they are the spouse dependent
            isEdit: true,
            isReinstatement: reinstatement,
            modalTitle: reinstatement ? "Reinstate Dependent" : "Edit Dependent",
            dialogOpen: true,
            dependentToRemoveID: false,
            firstnameError: false,
            lastnameError: false,
            birthdateError: false,
            cohabError: false,
            relationshipError: false,
            emailError: false,
            options,
            isDisabled: dependent.isDisabled,
            hasProvincialHealthCoverage: dependent.hasProvincialHealthCoverage,
            dateOfMarriage: dependent.dateOfMarriage === '' ? null : Services.dates.formatIncomingDate(dependent.dateOfMarriage),
            primaryBenefits: Array.isArray(dependent.primaryBenefits) ? dependent.primaryBenefits : []
        });
    };

    _showManageBenefits = async (dependent) => {
        const terminatedBenefits = await this.props.dispatch(getDependentTerminatedBenefits(this.props.user.userID, dependent.dependentID));

        this.setState({
            dependentID: dependent.dependentID,
            firstname: dependent.firstname,
            lastname: dependent.lastname,
            manageBenefitsDialogOpen: true,
            terminatedBenefits: terminatedBenefits || [],
            benefitsToTerminate: [],
            benefitsToReinstate: []
        });
    };

    _sendForgotPassword = async (dependent) => {
        if (!dependent.email) {
            this.props.dispatch(notifier.displayError({ verbiage: 'Dependent has no email address.' }));
            return;
        }

        const body = { email: dependent.email };
        await this.props.dispatch(resetDependentPassword(dependent.parentID, body));
    };

    /**
     * Called on save dependent
     * @private
     */
    _ageValidation = () => {
        let { birthdate, relationship, isDisabled } = this.state;
        // Calculate age in years
        const age = moment().utc().diff(birthdate, 'years');

        if (age > 25 && !isDisabled && (relationship === 'Child' || relationship === 'Grandchild')) {
            this.setState({
                birthdateError: 'Dependent children must be younger than 26 years old.'
            });

            this.props.dispatch(notifier.displayError({ verbiage: 'Dependent children must be younger than 26 years old.' }));

            return false;
        }

        return true;
    };

    _validateEffectiveDate = async () => {
        const effectiveDateError = await Services.validateInput('effectiveDate', this.state.effectiveDate);

        await this.setState({
            effectiveDateError
        });

        if (effectiveDateError) {
            return false;
        }

        return true;
    };

    /**
     * Called after age validation
     * @private
     */
    _save = async () => {
        const isEffectiveDateValid = await this._validateEffectiveDate();

        if (!isEffectiveDateValid) {
            return false;
        }

        this._closeEffectiveDateDialog();

        let {
            firstname,
            lastname,
            gender,
            birthdate,
            cohabDate,
            relationship,
            email,
            dependentID,
            employer,
            insuranceCarrier,
            dependentCertNumber,
            spouseCoverage,
            isStudent,
            isDisabled,
            dateOfMarriage,
            hasProvincialHealthCoverage,
            primaryBenefits
        } = this.state;

        let success = false;
        const data = {
            firstname,
            lastname,
            gender,
            relationship,
            isStudent: Boolean(isStudent),
            birthdate: Services.dates.formatOutgoingDate(birthdate),
            cohabDate: relationship.toUpperCase() === 'COMMON LAW' ? cohabDate === '' ? null : Services.dates.formatOutgoingDate(cohabDate) : null,
            employer: employer === '' ? null : employer,
            insuranceCarrier: insuranceCarrier === '' ? null : insuranceCarrier,
            dependentCertNumber: dependentCertNumber === '' ? null : dependentCertNumber,
            spouseCoverage: false,
            effectiveDate: dates.getUTC(this.state.effectiveDate),
            isDisabled: Boolean(isDisabled),
            dateOfMarriage: relationship.toUpperCase() === 'SPOUSE' ? dateOfMarriage === '' ? null : Services.dates.formatOutgoingDate(dateOfMarriage) : null,
            hasProvincialHealthCoverage: Boolean(hasProvincialHealthCoverage),
            primaryBenefits
        };

        if (this.state.isReinstatement) {
            data.status = 1;
        }

        if (email && email.length > 0) {
            data.email = email;
        }

        if (spouseCoverage !== false && spouseCoverage !== undefined) {
            data.spouseCoverage = spouseCoverage;
        }

        if (this.state.isEdit) {
            success = await this.props.dispatch(editDependent(this.props.user.userID, dependentID, data));
        } else {
            success = await this.props.dispatch(addDependent(this.props.user.userID, data));
        }

        if (success) {
            this._close();

            if (relationship.toUpperCase() !== 'SPOUSE') {
                this.setState({
                    hasSpouse: false
                });
            }

            return true;
        }

        // reset on error to the dialog form view
        this.setState({
            dialogOpen: true,
            isReinstatement: false
        });

        return false;
    };

    _remove = async () => {
        this.setState({
            showTerminationEffectiveDateDialog: false
        });

        const success = await this.props.dispatch(removeDependent(this.props.user.userID, this.state.dependentToRemoveID, dates.getUTC(this.state.effectiveDate)));

        if (success) {
            this._close();
        }

        return null;
    };

    _showTerminationEffectiveDateDialog = async (dependentToRemoveID) => {
        await this.setState({
            dependentToRemoveID
        });

        // if the account is not yet setup then we don't need to ask for an effective date
        if (this.props.meta.accountEffDate === null) {
            return this._remove();
        }

        this.setState({
            showTerminationEffectiveDateDialog: true
        });

        return null;
    };

    _hideTerminationEffectiveDateDialog = () => {
        this.setState({
            showTerminationEffectiveDateDialog: false
        });
    };

    _toggleTerminateBenefit = (benefit) => {
        const isSelected = this.state.benefitsToTerminate.find((item) => item === benefit);

        if (isSelected) {
            this.setState({
                benefitsToTerminate: this.state.benefitsToTerminate.filter((item) => item !== benefit)
            });
        } else {
            this.setState({
                benefitsToTerminate: [ ...this.state.benefitsToTerminate, benefit ]
            });
        }
    };

    _toggleReinstateBenefit = (benefit) => {
        const isSelected = this.state.benefitsToReinstate.find((item) => item === benefit);

        if (isSelected) {
            this.setState({
                benefitsToReinstate: this.state.benefitsToReinstate.filter((item) => item !== benefit)
            });
        } else {
            this.setState({
                benefitsToReinstate: [ ...this.state.benefitsToReinstate, benefit ]
            });
        }
    };

    _terminateDependentBenefits = async () => {
        const isEffectiveDateValid = await this._validateEffectiveDate();

        if (!isEffectiveDateValid) {
            return false;
        }

        const success = await this.props.dispatch(terminateDependentBenefits(this.props.user.userID, this.state.dependentID, this.state.benefitsToTerminate, dates.getUTC(this.state.effectiveDate)));

        if (success) {
            window.location.reload();
        }

        return null;
    };

    _reinstateDependentBenefits = async () => {
        const isEffectiveDateValid = await this._validateEffectiveDate();

        if (!isEffectiveDateValid) {
            return false;
        }

        const success = await this.props.dispatch(reinstateDependentBenefits(this.props.user.userID, this.state.dependentID, this.state.benefitsToReinstate, dates.getUTC(this.state.effectiveDate)));

        if (success) {
            window.location.reload();
        }

        return null;
    };

    render() {
        const styles = {
            bubbleListSelector: {
                margin: '10px 0',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px'
            },
            bubble: {
                fontSize: '12px',
                padding: '4px 13px',
                minWidth: '0',
                margin: '0'
            }
        };
        const { dependents } = this.props;
        let dependentToTerminate = null;

        if (this.state.showTerminationEffectiveDateDialog) {
            dependentToTerminate = dependents.find((dependent) => dependent.dependentID === this.state.dependentToRemoveID);
        }

        if (!dependents) {
            return null;
        }

        const isSpendingAccountOnly = this.props.meta?.isSpendingAccountOnly === 1; 
        // show the email field for users who previously entered an email for their dependent, and now are in a spending account only class
        const hasGrandFatheredEmail = isSpendingAccountOnly && Boolean(this.state.email);
        const showEmailField = !isSpendingAccountOnly || hasGrandFatheredEmail;

        return (
            <div>
                <div>
                    {
                        dependents.length === 0 ?
                            <Text testid="dependents" light medium>
                                They have not defined any dependents.
                            </Text>
                            :
                            dependents.map((dependent, i) => (
                                <PersonBlock
                                    testid={i === 0 ? "dependents" : null}
                                    key={dependent.dependentID}
                                    firstname={dependent.firstname}
                                    lastname={dependent.lastname}
                                    relationship={ dependent.isSurvivorBenefitParent ? 'Survivor Benefit' : dependent.relationship}
                                    parentID={dependent.parentID}
                                    status={dependent.status}
                                    termDate={dependent.termDate}
                                    type="dependent"
                                    edit={() => this._edit(dependent)}
                                    remove={() => this._showTerminationEffectiveDateDialog(dependent.dependentID)}
                                    reinstate={() => this._edit(dependent, true)}
                                    manageBenefits={() => this._showManageBenefits(dependent)}
                                    sendForgotPassword={() => this._sendForgotPassword(dependent)}
                                    accessType={ this.props.accessType }
                                />
                            ))
                    }

                    <PermissionsComponent
                        permission="Dependents"
                        accessType={this.props.accessType}
                        level="write"
                    >
                        <Fab
                            id="add-dependents-fab"
                            aria-label="Add"
                            onClick={this._add}
                            style={{
                                backgroundColor: Theme.colors.brandGreen,
                                position: 'fixed',
                                bottom: '5%',
                                right: '5%',
                                zIndex: 500
                            }}
                        >
                            <AddIcon style={{ color: Theme.colors.surface }} />
                        </Fab>
                    </PermissionsComponent>

                    <Modal
                        testid="add-edit-dependent-modal"
                        open={ this.state.dialogOpen }
                        onClose={ this._close }
                        title={ this.state.modalTitle }
                        primaryButton={
                            {
                                label: this.state.isReinstatement ? "Reinstate Dependent" : "Save Dependent",
                                onClick: this._openEffectiveDateDialog
                            }
                        }
                    >
                        <React.Fragment>
                            {
                                !this.state.isReinstatement &&
                                this.state.status.toUpperCase() === 'TERMINATED' &&
                                    <Text
                                        align="left"
                                        small
                                        display="inline-block"
                                    >
                                        This dependent is terminated.  Changes made here will not reinstate the dependent.
                                    </Text>
                            }

                            {
                                this.state.relationship.toUpperCase() === 'CHILD' &&
                                    <Text
                                        align="left"
                                        small
                                        display="inline-block"
                                    >
                                        Note: To be a valid child or grandchild dependent older than 21, they must be a fulltime student attending a recognized college/university, unmarried and not working fulltime.
                                    </Text>
                            }

                            <TextInput
                                label="First Name"
                                value={this.state.firstname}
                                autoFocus
                                onBlur={() => this._onBlur('firstname')}
                                onChange={(value) => this._handleInputChange('firstname', value)}
                                error={Boolean(this.state.firstnameError)}
                                helperText={this.state.firstnameError}
                                fullWidth
                            />

                            <TextInput
                                label="Last Name"
                                value={this.state.lastname}
                                onBlur={() => this._onBlur('lastname')}
                                onChange={(value) => this._handleInputChange('lastname', value)}
                                error={Boolean(this.state.lastnameError)}
                                helperText={this.state.lastnameError}
                                fullWidth
                            />

                            <TextInput
                                label="Gender"
                                value={this.state.gender}
                                fullWidth
                                onBlur={() => this._onBlur('gender')}
                                onChange={(value) => this._handleInputChange('gender', value)}
                                error={Boolean(this.state.genderError)}
                                helperText={this.state.genderError}
                                select
                                selectOptions={Services.selectFieldOptions.genderOptions()}
                            />

                            <DateInput
                                label="Date of Birth"
                                value={this.state.birthdate}
                                onBlur={() => this._onBlur('birthdate')}
                                onChange={(value) => this._handleInputChange('birthdate', value)}
                                error={Boolean(this.state.birthdateError)}
                                helperText={this.state.birthdateError}
                                fullWidth
                            />

                            <TextInput
                                label="Relationship"
                                value={this.state.relationship}
                                onBlur={() => this._onBlur('relationship')}
                                onChange={(value) => this._handleSelectChange('relationship', value, this.state.dependentID)}
                                error={Boolean(this.state.relationshipError)}
                                helperText={this.state.relationshipError}
                                select
                                selectOptions={this.state.options}
                                fullWidth
                            />

                            {
                                this.state.relationship.toUpperCase() === 'COMMON LAW' &&
                                    <DateInput
                                        label="Co-hab Date (YYYY/MM/DD)"
                                        value={this.state.cohabDate}
                                        onBlur={() => this._onBlur('cohabDate')}
                                        onChange={(value) => this._handleInputChange('cohabDate', value)}
                                        error={Boolean(this.state.cohabDateError)}
                                        helperText={this.state.cohabDateError}
                                        fullWidth
                                    />
                            }

                            {
                                this.state.relationship.toUpperCase() === 'SPOUSE' &&
                                <DateInput
                                    label="Date of Marriage (YYYY/MM/DD)"
                                    value={ this.state.dateOfMarriage }
                                    onBlur={() => this._onBlur('dateOfMarriage')}
                                    onChange={(value) => this._handleInputChange('dateOfMarriage', value)}
                                    error={ Boolean(this.state.dateOfMarriageError) }
                                    helperText={ this.state.dateOfMarriageError }
                                    fullWidth
                                />
                            }

                            {
                                showEmailField && (
                                    <TextInput
                                        label="Email (Optional)"
                                        value={this.state.email}
                                        onBlur={() => this._onBlur('email')}
                                        onChange={(value) => this._handleInputChange('email', value)}
                                        error={Boolean(this.state.emailError)}
                                        helperText={this.state.emailError}
                                        fullWidth
                                    />
                                )
                            }

                            {
                                this.state.relationship.toUpperCase() === 'CHILD' &&
                                <>
                                    <Text>
                                            Does this Dependent have Provincial Health Coverage?
                                    </Text>

                                    <BubbleSelectBlock
                                        first={{
                                            title: 'Yes',
                                            selected: Boolean(this.state.hasProvincialHealthCoverage) === true
                                        }}
                                        second={{
                                            title: 'No',
                                            selected: Boolean(this.state.hasProvincialHealthCoverage) === false
                                        }}
                                        onBlur={() => this._onBlur('hasProvincialHealthCoverage')}
                                        handleChange={ async () => {
                                            await this._handleInputChange('hasProvincialHealthCoverage', !this.state.hasProvincialHealthCoverage, true);
                                        }}
                                    />
                                    
                                    <FormControl component="fieldset">
                                        <Text
                                            medium
                                            light
                                            margin="10px 0 10px 0"
                                        >
                                            Is your dependent child currently enrolled as a full time student at a recognized institution?
                                        </Text>

                                        <BubbleSelectBlock
                                            first={{
                                                title: 'Yes',
                                                selected: Boolean(this.state.isStudent) === true,
                                                field: 'childStudentYes'
                                            }}
                                            second={{
                                                title: 'No',
                                                selected: Boolean(this.state.isStudent) === false,
                                                field: 'childStudentNo'
                                            }}
                                            onBlur={() => this._onBlur('isStudent')}
                                            handleChange={ async (field) => {
                                                if (field === 'childStudentYes') {
                                                    await this._handleInputChange('isStudent', true, true);
                                                } else {
                                                    await this._handleInputChange('isStudent', false, true);
                                                }
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl component="fieldset">
                                        <Text
                                            medium
                                            light
                                            margin="10px 0 10px 0"
                                        >
                                            Does this dependent have a disability?
                                        </Text>

                                        <BubbleSelectBlock
                                            first={{
                                                title: 'Yes',
                                                selected: Boolean(this.state.isDisabled) === true,
                                                field: 'isDisabledYes'
                                            }}
                                            second={{
                                                title: 'No',
                                                selected: Boolean(this.state.isDisabled) === false,
                                                field: 'isDisabledNo'
                                            }}
                                            onBlur={() => this._onBlur('isDisabled')}
                                            handleChange={ async (field) => {
                                                if (field === 'isDisabledYes') {
                                                    await this._handleInputChange('isDisabled', true, true);
                                                } else {
                                                    await this._handleInputChange('isDisabled', false, true);
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </>
                            }

                            {
                                (this.state.relationship.toUpperCase() === 'SPOUSE' || this.state.relationship.toUpperCase() === 'COMMON LAW') &&
                                    <React.Fragment>
                                        <FormControl component="fieldset">
                                            <Text
                                                medium
                                                light
                                                margin="10px 0 10px 0"
                                            >
                                                Does this Dependent have Provincial Health Coverage?
                                            </Text>

                                            <BubbleSelectBlock
                                                first={{
                                                    title: 'Yes',
                                                    selected: Boolean(this.state.hasProvincialHealthCoverage) === true
                                                }}
                                                second={{
                                                    title: 'No',
                                                    selected: Boolean(this.state.hasProvincialHealthCoverage) === false
                                                }}
                                                onBlur={() => this._onBlur('hasProvincialHealthCoverage')}
                                                handleChange={ async () => {
                                                    await this._handleInputChange('hasProvincialHealthCoverage', !this.state.hasProvincialHealthCoverage, true);
                                                }}
                                            />
                                        </FormControl>

                                        <FormControl component="fieldset">
                                            <Text
                                                medium
                                                light
                                                margin="10px 0 10px 0"
                                            >
                                                Does this Dependent have comparable coverage through their organization?
                                            </Text>

                                            <BubbleSelectBlock
                                                first={{
                                                    title: 'Yes',
                                                    selected: Boolean(this.state.spouseCoverage) === true,
                                                    field: 'spouseCoverageYes'
                                                }}
                                                second={{
                                                    title: 'No',
                                                    selected: Boolean(this.state.spouseCoverage) === false,
                                                    field: 'spouseCoverageNo'
                                                }}
                                                onBlur={() => this._onBlur('spouseCoverage')}
                                                handleChange={ async (field) => {
                                                    if (field === 'spouseCoverageYes') {
                                                        await this._handleInputChange('spouseCoverage', true, true);
                                                    } else {
                                                        await this._handleInputChange('spouseCoverage', false, true);
                                                    }
                                                }}
                                            />
                                        </FormControl>

                                        {
                                            Boolean(this.state.spouseCoverage) === true &&
                                                <React.Fragment>
                                                    <TextInput
                                                        label="Dependent's Organization"
                                                        value={this.state.employer}
                                                        onBlur={() => this._onBlur('employer')}
                                                        onChange={(value) => this._handleInputChange('employer', value)}
                                                        error={ this.state.employerError }
                                                        fullWidth
                                                    />

                                                    <TextInput
                                                        label="Dependent's Current Insurance Carrier"
                                                        value={this.state.insuranceCarrier}
                                                        fullWidth
                                                        onBlur={() => this._onBlur('insuranceCarrier')}
                                                        onChange={(value) => this._handleInputChange('insuranceCarrier', value)}
                                                        select={
                                                            (!this.state.insuranceCarrier || insuranceOptions.indexOf(this.state.insuranceCarrier) > -1)
                                                            && this.state.insuranceCarrier !== ''
                                                        }
                                                        selectOptions={insuranceOptions}
                                                        error={ this.state.insuranceCarrierError }
                                                    />

                                                    <TextInput
                                                        label="Dependent's Certificate Number"
                                                        value={this.state.dependentCertNumber}
                                                        fullWidth
                                                        onBlur={() => this._onBlur('dependentCertNumber')}
                                                        onChange={(value) => this._handleInputChange('dependentCertNumber', value)}
                                                        error={ this.state.dependentCertNumberError }
                                                    />

                                                    <Text
                                                        medium
                                                        light
                                                        margin="10px 0 10px 0"
                                                    >
                                                        Benefits
                                                    </Text>
                                                    <Text small>Please indicate all the benefits the dependent has</Text>
                                                    <div style={ styles.bubbleListSelector }>
                                                        {
                                                            primaryBenefitsNames.map((option) => (
                                                                <Bubble
                                                                    customStyle={ styles.bubble }
                                                                    key={ option }
                                                                    label={ option }
                                                                    isSelected={ this.state.primaryBenefits.includes(option) }
                                                                    onClick={() => this._handleBubbleListSelector(option)}
                                                                ></Bubble>
                                                            ))
                                                        }
                                                    </div>
                                                </React.Fragment>
                                        }
                                    </React.Fragment>
                            }
                        </React.Fragment>
                    </Modal>
                </div>

                <Modal
                    open={ this.state.manageBenefitsDialogOpen }
                    onClose={ this._close }
                    title={ `Manage Dependent Benefits (${ this.state.firstname } ${ this.state.lastname })` }
                    medium
                >
                    <Text medium bold>
                        Active Benefits
                    </Text>

                    <Text small margin="5px 0 10px 0">
                        Select which benefits you would like to terminate for this dependent.
                    </Text>

                    <div style={{ display: 'flex', flex: 1, flexWrap: 'wrap', flexDirection: 'row' }}>
                        {
                            Object.keys(benefits).map((benefit) => {
                                if (this.state.terminatedBenefits.indexOf(benefit) !== -1) {
                                    return null;
                                }

                                return (
                                    <Bubble
                                        label={ benefits[benefit] }
                                        clickable={ true }
                                        isSelected={ this.state.benefitsToTerminate.find((item) => item === benefit) }
                                        onClick={ () => this._toggleTerminateBenefit(benefit)}
                                    />
                                );
                            })
                        }
                    </div>

                    <Button
                        onClick={() => this.setState({ terminateBenefitsEffectiveDateDialogOpen: true }) }
                        color="primary"
                        marginTop={2}
                        marginBottom={2}
                        customStyle={{
                            alignSelf: "flex-start"
                        }}
                    >
                        Terminate Benefits
                    </Button>

                    <div style={{
                        borderBottom: `1px solid #eee`,
                        width: '100%',
                        margin: '10px auto 10px auto'
                    }} />

                    <Text medium bold margin="20px 0 0 0">
                        Terminated Benefits
                    </Text>

                    <Text small margin="5px 0 10px 0">
                        Select which benefits you would like to reinstate for this dependent.
                    </Text>

                    {
                        this.state.terminatedBenefits.length === 0 ?
                            <Text small bold>
                                No benefits have been terminated.
                            </Text>
                            :
                            <div style={{ display: 'flex', flex: 1, flexWrap: 'wrap', flexDirection: 'row' }}>
                                {
                                    this.state.terminatedBenefits.map((benefit) => (
                                        <Bubble
                                            label={ benefits[benefit] }
                                            clickable={ true }
                                            isSelected={ this.state.benefitsToReinstate.find((item) => item === benefit) }
                                            onClick={ () => this._toggleReinstateBenefit(benefit)}
                                        />
                                    ))
                                }
                            </div>
                    }

                    {
                        this.state.terminatedBenefits.length > 0 &&
                            <Button
                                onClick={() => this.setState({ reinstateBenefitsEffectiveDateDialogOpen: true }) }
                                color="primary"
                                marginTop={2}
                                marginBottom={2}
                                customStyle={{
                                    alignSelf: "flex-start"
                                }}
                            >
                                Reinstate Benefits
                            </Button>
                    }
                </Modal>

                <Modal
                    open={this.state.terminateBenefitsEffectiveDateDialogOpen}
                    transitionComponent={this._transition}
                    onClose={() => this.setState({ terminateBenefitsEffectiveDateDialogOpen: false }) }
                    title="Set Effective Date"
                    primaryButton={{
                        label: "Terminate",
                        onClick: this._terminateDependentBenefits
                    }}
                >
                    <Text medium>
                        Please provide the date you would like the benefit termination(s) to be effective:
                    </Text>

                    <DateInput
                        label="Effective Date"
                        value={this.state.effectiveDate}
                        onBlur={() => this._onBlur('effectiveDate')}
                        onChange={(e) => this._handleInputChange('effectiveDate', e)}
                        error={Boolean(this.state.effectiveDateError)}
                        helperText={this.state.effectiveDateError}
                        fullWidth
                        required
                    />

                    <Text medium margin="10px 0 0 0">
                        By clicking the <Text customStyle={{ display: 'inline' }} medium bold>Terminate</Text> button below, the benefit termination(s) will be applied and effective on the date provided above. Back dated changes will have adjustments applied using current rates.
                    </Text>
                </Modal>

                <Modal
                    open={this.state.reinstateBenefitsEffectiveDateDialogOpen}
                    transitionComponent={this._transition}
                    onClose={() => this.setState({ reinstateBenefitsEffectiveDateDialogOpen: false }) }
                    title="Set Effective Date"
                    primaryButton={{
                        label: "Reinstate",
                        onClick: this._reinstateDependentBenefits
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        Please provide the date you would like the benefit reinstatement(s) to be effective:
                    </Text>

                    <DateInput
                        label="Effective Date"
                        value={this.state.effectiveDate}
                        onBlur={() => this._onBlur('effectiveDate')}
                        onChange={(e) => this._handleInputChange('effectiveDate', e)}
                        error={Boolean(this.state.effectiveDateError)}
                        helperText={this.state.effectiveDateError}
                        fullWidth
                        required
                    />

                    <Text medium margin="10px 0 0 0">
                        By clicking the <Text customStyle={{ display: 'inline' }} medium bold>Reinstate</Text> button below, the benefit reinstatement(s) will be applied and effective on the date provided above. Back dated changes will have adjustments applied using current rates.
                    </Text>
                </Modal>

                <Modal
                    open={this.state.showTerminationEffectiveDateDialog}
                    transitionComponent={this._transition}
                    onClose={this._hideTerminationEffectiveDateDialog}
                    title="Set Effective Date"
                    primaryButton={{
                        label: "Terminate",
                        onClick: this._remove
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        Please provide the date you would like the termination to be effective:
                    </Text>

                    <DateInput
                        label="Effective Date"
                        value={this.state.effectiveDate}
                        onBlur={() => this._onBlur('effectiveDate')}
                        onChange={(e) => this._handleInputChange('effectiveDate', e)}
                        error={Boolean(this.state.effectiveDateError)}
                        helperText={this.state.effectiveDateError}
                        fullWidth
                        required
                    />

                    <Text medium margin="10px 0 0 0">
                        By clicking the <Text customStyle={{ display: 'inline' }} medium bold>Terminate</Text> button below, the termination for <Text customStyle={{ display: 'inline' }} medium bold>{ `${ dependentToTerminate?.firstname } ${ dependentToTerminate?.lastname }` }</Text> will be applied and effective on the date provided above. Back dated changes will have adjustments applied using current rates.
                    </Text>
                </Modal>

                <Modal
                    open={this.state.showEffectiveDateDialog}
                    transitionComponent={this._transition}
                    onClose={this._closeEffectiveDateDialog}
                    title="Set Effective Date"
                    primaryButton={{
                        label: "Submit",
                        onClick: this._save
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        Please provide the date you would like the change(s) to be effective:
                    </Text>

                    <DateInput
                        label="Effective Date"
                        value={this.state.effectiveDate}
                        onBlur={() => this._onBlur('effectiveDate')}
                        onChange={(e) => this._handleInputChange('effectiveDate', e)}
                        error={Boolean(this.state.effectiveDateError)}
                        helperText={this.state.effectiveDateError}
                        fullWidth
                        required
                    />

                    <Text medium margin="10px 0 0 0">
                        By clicking Submit below, the changes made will be applied and effective on the date provided above. Back dated changes will have adjustments applied using current rates.
                    </Text>
                </Modal>

                <span id="user-dependent" />
            </div>
        );
    }
}

Dependents.propTypes = {
    dependents: PropTypes.array,
    user: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    dependents: state.users.currentUser.dependents,
    user: state.users.currentUser.meta,
    accessType: state.master.access
});

export default connect(mapStateToProps)(Dependents);